/** ***********
*
* <user-right.effects.ts v1.0>
*
* Copyright (c) 2022 AIRBUS OPERATIONS
*
* Contributors:
*  NG6C740 [thomas.pereira.external@airbus.com]
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*
* All rights reserved.
**************/

import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarComponent } from 'src/app/components/shared/snackbar/snackbar';
import { ErrorTranslationService } from 'src/app/services/error-translation.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { combineLatest, concat, forkJoin, from, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, exhaustMap, map, switchMap } from 'rxjs/operators';
import { UserRightService } from 'src/app/services/user-right.service';
import * as featureActions from '../actions/user-right.action';
import { Store } from '@ngrx/store';
import { UserService } from 'src/app/services/user.service';
import { successFetchAllProfiles } from '../actions/profile.action';
import { ParentFamilyService } from 'src/app/services/parent-family.service';
import { FamilyService } from 'src/app/services/family.service';
import { CacheService } from 'src/app/services/cache.service';
import { ObjectStore } from 'src/app/models/cache';

export interface EffectResult {
  result: 'success' | 'error';
  action: featureActions.ActionTypes;
  payload?: any;
}

@Injectable()
export class UserRightEffects {
  public successUpdateSpecificRole$ = createEffect(() => { return this._actions$.pipe(
    ofType<featureActions.UpdateSpecificRole>(featureActions.ActionTypes.UPDATE_SPECIFIC_ROLE),
    exhaustMap((action) => this._userRightService.updateSpecificRole(action.payload).pipe(
      map((user) => {
        const text: string = this._translate.instant('BANNER_SUCCESS_EDIT', { value: user.email });
        this._snackBar.open(text, 'green-snackbar', 5000);
        this._effectSubject.next({
          result: 'success',
          action: featureActions.ActionTypes.UPDATE_SPECIFIC_ROLE,
        });
        this._subscription.add(this._userService.getProfiles().subscribe((profiles) => {
          this.store.dispatch(successFetchAllProfiles({ profile: profiles }));
        }));
        return new featureActions.SuccessUpdateSpecificRole(user);
      }),
      catchError((err) => {
        this._errorTranslationService.handleError(err, 'BANNER_FAIL_INTERNAL_UPDATE');
        this._effectSubject.next({
          result: 'error',
          action: featureActions.ActionTypes.UPDATE_SPECIFIC_ROLE,
        });
        return of(new featureActions.ErrorUpdateSpecificRole());
      }),
    )),
  ) });

  public fetchGroups$ = createEffect(() => { return this._actions$.pipe(
    ofType<featureActions.FetchGroups>(featureActions.ActionTypes.FETCH_GROUPS),
    exhaustMap(() => this._userRightService.getUserGroups$().pipe(
      map((groups) => {
        this._effectSubject.next({
          result: 'success',
          action: featureActions.ActionTypes.FETCH_GROUPS
        });
        return new featureActions.SuccessFetchGroups(groups);
      }),
      catchError((err) => {
        this._errorTranslationService.handleError(err, 'BANNER_FAIL_FETCH_GROUPS');
        this._effectSubject.next({
          result: 'error',
          action: featureActions.ActionTypes.FETCH_GROUPS
        });
        return of(new featureActions.ErrorFetchGroups());
      }),
    )),
  ) });

  public updateGroup$ = createEffect(() => { return this._actions$.pipe(
    ofType<featureActions.UpdateGroup>(featureActions.ActionTypes.UPDATE_GROUP),
    exhaustMap(({ payload }) => this._userRightService.updateGroup$(payload.group, payload.users, payload.families).pipe(
      map((group) => {
        this._effectSubject.next({
          result: 'success',
          action: featureActions.ActionTypes.UPDATE_GROUP
        });
        return new featureActions.SuccessUpdateGroup(group);
      }),
      catchError((err) => {
        this._errorTranslationService.handleError(err, 'BANNER_FAIL_UPDATE_GROUP');
        this._effectSubject.next({
          result: 'error',
          action: featureActions.ActionTypes.UPDATE_GROUP
        });
        return of(new featureActions.ErrorUpdateGroup());
      }),
    )),
  ) });

  public transferGroupOwnership$ = createEffect(() => {
    return this._actions$.pipe(
        ofType(featureActions.transferGroupOwnership),
        exhaustMap(({ group, transferedTo }) => this._userRightService.transferGroupOwnership$(group, transferedTo).pipe(
            map((group) => {
                this._effectSubject.next({
                    result: 'success',
                    action: featureActions.ActionTypes.TRANSFER_GROUP_OWNERSHIP,
                    payload: group,
                });
                return featureActions.successTransferGroupOwnership({ group });
            }),
            catchError((err) => {
                this._errorTranslationService.handleError(err, 'BANNER_FAIL_TRANSFER_GROUP_OWNERSHIP');
                this._effectSubject.next({
                    result: 'error',
                    action: featureActions.ActionTypes.TRANSFER_GROUP_OWNERSHIP
                });
                return of(featureActions.errorTransferGroupOwnership());
            }),
        ))
    )
  })

  public createGroup$ = createEffect(() => { return this._actions$.pipe(
    ofType<featureActions.CreateGroup>(featureActions.ActionTypes.CREATE_GROUP),
    exhaustMap(({ payload }) => this._userRightService.createGroup$(payload.group, payload.users, payload.families).pipe(
      map((group) => {
        this._effectSubject.next({
          result: 'success',
          action: featureActions.ActionTypes.CREATE_GROUP
        });
        return new featureActions.SuccessCreateGroup(group);
      }),
      catchError((err) => {
        this._errorTranslationService.handleError(err, 'BANNER_FAIL_CREATE_GROUP');
        this._effectSubject.next({
          result: 'error',
          action: featureActions.ActionTypes.CREATE_GROUP
        });
        return of(new featureActions.ErrorCreateGroup());
      }),
    )),
  ) });

  public deleteGroup$ = createEffect(() => { return this._actions$.pipe(
    ofType<featureActions.DeleteGroup>(featureActions.ActionTypes.DELETE_GROUP),
    exhaustMap(({ payload }) => this._userRightService.deleteGroup$(payload.group).pipe(
      map((groupId) => {
        this._effectSubject.next({
          result: 'success',
          action: featureActions.ActionTypes.DELETE_GROUP
        });
        return new featureActions.SuccessDeleteGroup({ groupId });
      }),
      catchError((err) => {
        this._errorTranslationService.handleError(err, 'BANNER_FAILDELETE_GROUP');
        this._effectSubject.next({
          result: 'error',
          action: featureActions.ActionTypes.DELETE_GROUP
        });
        return of(new featureActions.ErrorDeleteGroup());
      }),
    )),
  ) });

  public fetchUsersOfGroups$ = createEffect(() => { return this._actions$.pipe(
    ofType<featureActions.FetchUsersOfGroup>(featureActions.ActionTypes.FETCH_USERS_OF_GROUP),
    exhaustMap(({ payload }) => forkJoin([
      of(payload.group),
      this._userRightService.getUsersOfGroup$(payload.group),
    ]).pipe(
      map(([group, users]) => {
        this._effectSubject.next({
          result: 'success',
          action: featureActions.ActionTypes.FETCH_USERS_OF_GROUP
        });
        return new featureActions.SuccessFetchUsersOfGroup({ group, users });
      }),
      catchError((err) => {
        this._errorTranslationService.handleError(err, 'BANNER_FAIL_FETCH_USERS_OF_GROUP');
        this._effectSubject.next({
          result: 'error',
          action: featureActions.ActionTypes.FETCH_USERS_OF_GROUP
        });
        return of(new featureActions.ErrorFetchUsersOfGroup());
      }),
    )),
  ) });

  public fetchFamiliesOfGroups$ = createEffect(() => { return this._actions$.pipe(
    ofType<featureActions.FetchFamiliesOfGroup>(featureActions.ActionTypes.FETCH_FAMILIES_OF_GROUP),
    exhaustMap(({ payload }) => forkJoin([
      of(payload.group),
      this._userRightService.getFamiliesOfGroup$(payload.group),
    ]).pipe(
      map(([group, families]) => {
        this._effectSubject.next({
          result: 'success',
          action: featureActions.ActionTypes.FETCH_FAMILIES_OF_GROUP
        });
        return new featureActions.SuccessFetchFamiliesOfGroup({ group, families });
      }),
      catchError((err) => {
        this._errorTranslationService.handleError(err, 'BANNER_FAIL_FETCH_FAMILIES_OF_GROUP');
        this._effectSubject.next({
          result: 'error',
          action: featureActions.ActionTypes.FETCH_FAMILIES_OF_GROUP
        });
        return of(new featureActions.ErrorFetchFamiliesOfGroup());
      }),
    )),
  ) });

  public addUsersToGroup$ = createEffect(() => { return this._actions$.pipe(
    ofType<featureActions.AddUsersToGroup>(featureActions.ActionTypes.ADD_USERS_TO_GROUP),
    exhaustMap(({ payload }) => forkJoin([
      of(payload.group),
      this._userRightService.addUsersToGroup$(payload.group, payload.users),
    ]).pipe(
      map(([group, users]) => {
        this._effectSubject.next({
          result: 'success',
          action: featureActions.ActionTypes.ADD_USERS_TO_GROUP
        });
        return new featureActions.SuccessAddUsersToGroup({ group, users });
      }),
      catchError((err) => {
        this._errorTranslationService.handleError(err, 'BANNER_FAIL_ADD_USERS_TO_GROUP');
        this._effectSubject.next({
          result: 'error',
          action: featureActions.ActionTypes.ADD_USERS_TO_GROUP
        });
        return of(new featureActions.ErrorAddUsersToGroup());
      }),
    )),
  ) });

  public addFamiliesToGroup$ = createEffect(() => { return this._actions$.pipe(
    ofType<featureActions.AddFamiliesToGroup>(featureActions.ActionTypes.ADD_FAMILIES_TO_GROUP),
    exhaustMap(({ payload }) => forkJoin([
      of(payload.group),
      this._userRightService.addFamiliesToGroup$(payload.group, payload.families),
    ]).pipe(
      map(([group, families]) => {
        this._effectSubject.next({
          result: 'success',
          action: featureActions.ActionTypes.ADD_FAMILIES_TO_GROUP
        });
        return new featureActions.SuccessAddFamiliesToGroup({ group, families });
      }),
      catchError((err) => {
        this._errorTranslationService.handleError(err, 'BANNER_FAIL_ADD_FAMILIES_TO_GROUP');
        this._effectSubject.next({
          result: 'error',
          action: featureActions.ActionTypes.ADD_FAMILIES_TO_GROUP
        });
        return of(new featureActions.ErrorAddFamiliesToGroup());
      }),
    )),
  ) });

  public changeGroupFamiliesLevel$ = createEffect(() => { return this._actions$.pipe(
    ofType<featureActions.ChangeGroupFamiliesLevel>(featureActions.ActionTypes.CHANGE_GROUP_FAMILIES_LEVEL),
    exhaustMap(({ payload }) => forkJoin([
      of(payload.group),
      this._userRightService.changeGroupFamiliesLevel$(payload.group, payload.families),
    ]).pipe(
      map(([group, families]) => {
        this._effectSubject.next({
          result: 'success',
          action: featureActions.ActionTypes.CHANGE_GROUP_FAMILIES_LEVEL
        });
        return new featureActions.SuccessChangeGroupFamiliesLevel({ group, families });
      }),
      catchError((err) => {
        this._errorTranslationService.handleError(err, 'BANNER_FAIL_CHANGE_GROUP_FAMILIES_LEVEL');
        this._effectSubject.next({
          result: 'error',
          action: featureActions.ActionTypes.CHANGE_GROUP_FAMILIES_LEVEL
        });
        return of(new featureActions.ErrorChangeGroupFamiliesLevel());
      }),
    )),
  ) });

  public removeUsersFromGroup$ = createEffect(() => { return this._actions$.pipe(
    ofType<featureActions.RemoveUsersFromGroup>(featureActions.ActionTypes.REMOVE_USERS_FROM_GROUP),
    exhaustMap(({ payload }) => forkJoin([
      of(payload.group),
      this._userRightService.removeUsersFromGroup$(payload.group, payload.users)
    ]).pipe(
      map(([group, users]) => {
        this._effectSubject.next({
          result: 'success',
          action: featureActions.ActionTypes.REMOVE_USERS_FROM_GROUP
        });
        return new featureActions.SuccessRemoveUsersFromGroup({ group, users });
      }),
      catchError((err) => {
        this._errorTranslationService.handleError(err, 'BANNER_FAIL_REMOVE_USERS_FROM_GROUP');
        this._effectSubject.next({
          result: 'error',
          action: featureActions.ActionTypes.REMOVE_USERS_FROM_GROUP
        });
        return of(new featureActions.ErrorRemoveUsersFromGroup());
      }),
    )),
  ) });

  public removeFamiliesFromGroup$ = createEffect(() => { return this._actions$.pipe(
    ofType<featureActions.RemoveFamiliesFromGroup>(featureActions.ActionTypes.REMOVE_FAMILIES_FROM_GROUP),
    exhaustMap(({ payload }) => forkJoin([
      of(payload.group),
      this._userRightService.removeFamiliesFromGroup$(payload.group, payload.families)
    ]).pipe(
      map(([group, families]) => {
        this._effectSubject.next({
          result: 'success',
          action: featureActions.ActionTypes.REMOVE_FAMILIES_FROM_GROUP
        });
        return new featureActions.SuccessRemoveFamiliesFromGroup({ group, families });
      }),
      catchError((err) => {
        this._errorTranslationService.handleError(err, 'BANNER_FAIL_REMOVE_FAMILIE_FROM_GROUP');
        this._effectSubject.next({
          result: 'error',
          action: featureActions.ActionTypes.REMOVE_FAMILIES_FROM_GROUP
        });
        return of(new featureActions.ErrorRemoveFamiliesFromGroup());
      }),
    )),
  ) });

  public fetchGroupsOfUser$ = createEffect(() => { return this._actions$.pipe(
    ofType<featureActions.FetchGroupsOfUser>(featureActions.ActionTypes.FETCH_GROUPS_OF_USER),
    exhaustMap(({ payload }) => forkJoin([
        this._userRightService.getGroupOfUser$(payload.userId),
        this._userRightService.checkRequestExist$(payload.userId),
    ]).pipe(
      map(([group, requestedGroup]) => {
        this._effectSubject.next({
          result: 'success',
          action: featureActions.ActionTypes.FETCH_GROUPS_OF_USER
        });
        return new featureActions.SuccessFetchGroupsOfUser({ group, requestedGroup });
      }),
      catchError((err) => {
        this._errorTranslationService.handleError(err, 'BANNER_FAIL_FETCH_GROUPS_OF_USER');
        this._effectSubject.next({
          result: 'error',
          action: featureActions.ActionTypes.FETCH_GROUPS_OF_USER
        });
        return of(new featureActions.ErrorFetchGroupsOfUser());
      }),
    )),
  ) });

  public fetchListOfAllGroups$ = createEffect(() => { return this._actions$.pipe(
    ofType<featureActions.FetchListOfAllGroups>(featureActions.ActionTypes.FETCH_LIST_OF_ALL_GROUPS),
    exhaustMap(() => this._userRightService.getAllGroups$().pipe(
      map((group) => {
        this._effectSubject.next({
          result: 'success',
          action: featureActions.ActionTypes.FETCH_LIST_OF_ALL_GROUPS
        });
        return new featureActions.SuccessFetchListOfAllGroups({ group });
      }),
      catchError((err) => {
        this._errorTranslationService.handleError(err, 'BANNER_FAIL_FETCH_GROUPS_OF_USER');
        this._effectSubject.next({
          result: 'error',
          action: featureActions.ActionTypes.FETCH_LIST_OF_ALL_GROUPS
        });
        return of(new featureActions.ErrorFetchListOfAllGroups());
      }),
    )),
  ) });

  public raiseGroupRequest$ = createEffect(() => { return this._actions$.pipe(
    ofType<featureActions.RaiseGroupRequest>(featureActions.ActionTypes.RAISE_GROUP_REQUEST),
    exhaustMap(({ payload }) => this._userRightService.raiseGroupRequest$(payload.requestedGroup).pipe(
      map((requestedGroup) => {
        this._effectSubject.next({
          result: 'success',
          action: featureActions.ActionTypes.SUCCESS_RAISE_GROUP_REQUEST,
          payload: requestedGroup,
        });
        return new featureActions.SuccessRequestedGroup({ requestedGroup });
      }),
      catchError((err) => {
        this._errorTranslationService.handleError(err, 'BANNER_ERROR_REQUEST_GROUP');
        this._effectSubject.next({
          result: 'error',
          action: featureActions.ActionTypes.ERROR_RAISE_GROUP_REQUEST
        });
        return of(new featureActions.ErrorCreateGroupOfRequest());
      }),
    )),
  ) });

  public cancelGroupRequest$ = createEffect(() => { return this._actions$.pipe(
    ofType<featureActions.CancelGroupRequest>(featureActions.ActionTypes.CANCEL_GROUP_REQUEST),
    exhaustMap(({ payload }) => this._userRightService.cancelGroupRequest$(payload.groupData).pipe(
      map(() => {
        this._effectSubject.next({
          result: 'success',
          action: featureActions.ActionTypes.SUCCESS_CANCEL_GROUP_REQUEST
        });
        return new featureActions.SuccessDeleteRequest(payload);
      }),
      catchError((err) => {
        this._errorTranslationService.handleError(err, 'BANNER_FAIL_FETCH_GROUPS_OF_USER');
        this._effectSubject.next({
          result: 'error',
          action: featureActions.ActionTypes.ERROR_CANCEL_GROUP_REQUEST
        });
        return of(new featureActions.ErrorCancelGroupRequest());
        }),
      )),
    ) });

  public RemoveMyRightsRequest$ = createEffect(() => { return this._actions$.pipe(
    ofType<featureActions.RemoveMyRightsRequest>(featureActions.ActionTypes.DELETE_MY_RIGHT),
    switchMap(({ payload }) => forkJoin([
      of(payload.removeRight),
      this._userRightService.deleteMyRights$(payload.removeRight)
    ]).pipe(
      map(([removeRight]) => {
        this._snackBar.open(this._translate.instant('BANNER_SUCCESS_REMOVE_ROLES_OF_USER'), 'green-snackbar', 5000);
        this._effectSubject.next({
          result: 'success',
          action: featureActions.ActionTypes.DELETE_MY_RIGHT
        });
        return new featureActions.SuccessDeleteMyRights({ removeRight });
      }),
      catchError((err) => {
        this._errorTranslationService.handleError(err, 'BANNER_FAIL_REMOVE_ROLES_OF_USER');
        this._effectSubject.next({
          result: 'error',
          action: featureActions.ActionTypes.DELETE_MY_RIGHT
        });
        return of(new featureActions.ErrorRemoveUsersFromGroup());
      }),
    )),
  ) });

  public fetchUserRights$ = createEffect(() => { return this._actions$.pipe(
      ofType<featureActions.FetchMyRightsTree>(featureActions.ActionTypes.FETCH_MY_RIGHT_TREE),
      switchMap(() => {
        // Dispatch LoadParentFamilyRight as a separate action
        return concat(
          of(new featureActions.LoadParentFamilyRight()), 
          forkJoin({
            parentFamiliesWithRights: this._parentFamilyService.getParentFamiliesRights(),
            lastRefresh: this._cacheService.retrieveCacheStatus$().pipe(
              map(status => status.familyRight?.lastRefresh)
            )
          }).pipe(
            switchMap(({ parentFamiliesWithRights, lastRefresh }) => 
              from(this._cacheService.resetDatabase(false, ObjectStore.FamilyRight)).pipe(
                switchMap(() => this._familyService.getFamiliesRights(lastRefresh)),
                switchMap(familyRights =>
                  forkJoin([
                    this._cacheService.deleteData$(ObjectStore.FamilyRight, ...(familyRights.deletedData ?? []).map(({ id }) => id)),
                    this._cacheService.updateData$(ObjectStore.FamilyRight, ...(familyRights.updatedData ?? [])),
                    this._cacheService.createData$(ObjectStore.FamilyRight, ...(familyRights.createdData ?? []))
                  ])
                ),
                switchMap(() => this._cacheService.getData$(ObjectStore.FamilyRight)),
                map(familiesWithRights => {
                  this._effectSubject.next({
                    result: 'success',
                    action: featureActions.ActionTypes.FETCH_MY_RIGHT_TREE
                  });
                  return new featureActions.SuccessMyRightsTree(parentFamiliesWithRights, familiesWithRights);
                })
              )
            ),
            catchError((err) => {
              this._errorTranslationService.handleError(err, 'BANNER_FAIL_FETCH_RIGHT_TREE');
              this._effectSubject.next({
                result: 'error',
                action: featureActions.ActionTypes.FETCH_MY_RIGHT_TREE
              });
              return of(new featureActions.ErrorParentFamilyRight());
            })
          )
        );
      })
    )});
  

//  Delta call
//   public fetchUserRights$ = createEffect(() => { return this._actions$.pipe(
//     ofType<featureActions.FetchMyRightsTree>(featureActions.ActionTypes.FETCH_MY_RIGHT_TREE),
//     switchMap(() => {
//         this.store.dispatch(new featureActions.LoadParentFamilyRight());
//         return forkJoin([
//             this._parentFamilyService.getParentFamiliesRights(),
//             this._cacheService.retrieveCacheStatus$().pipe(
//                 map(status => status.familyRight?.lastRefresh),
//                 switchMap(lastRefresh => this._familyService.getFamiliesRights(lastRefresh)),
//                 switchMap(familyRights => combineLatest([
//                     this._cacheService.deleteData$(ObjectStore.FamilyRight, ...(familyRights.deletedData ?? []).map(({ id }) => id)),
//                     this._cacheService.updateData$(ObjectStore.FamilyRight, ...(familyRights.updatedData ?? [])),
//                     this._cacheService.createData$(ObjectStore.FamilyRight, ...(familyRights.createdData?? [])),
//                 ])),
//                 switchMap(() => this._cacheService.getData$(ObjectStore.FamilyRight)),
//             )
//         ]).pipe(
//             map(([parentFamiliesWithRights, familiesWithRights]) => {
//                 this._effectSubject.next({
//                     result: 'success',
//                     action: featureActions.ActionTypes.FETCH_MY_RIGHT_TREE
//                   });
//                   return new featureActions.SuccessMyRightsTree(parentFamiliesWithRights, familiesWithRights);
//             }),
//             catchError((err) => {
//                 this._errorTranslationService.handleError(err, 'BANNER_FAIL_FETCH_RIGHT_TREE');
//                 this._effectSubject.next({
//                 result: 'error',
//                 action: featureActions.ActionTypes.FETCH_MY_RIGHT_TREE
//                 });
//                 return of(new featureActions.ErrorParentFamilyRight());
//             }),
//         );
//     })
//   ) });

  private _effectSubject: Subject<EffectResult>;
  public effectSubject: Observable<EffectResult>;
  private _subscription: Subscription = new Subscription();
  constructor(
    private _userService: UserService,
    private _parentFamilyService: ParentFamilyService,
    private _familyService: FamilyService,
    private store: Store,
    private _userRightService: UserRightService,
    private _actions$: Actions,
    private _snackBar: SnackbarComponent,
    private _translate: TranslateService,
    private _errorTranslationService: ErrorTranslationService,
    private readonly _cacheService: CacheService,
  ) {
    this._effectSubject = new Subject<EffectResult>();
    this.effectSubject = this._effectSubject.asObservable();
  }
}
